import { useState, useEffect } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';
import { useAuth } from '../../auth/context/AuthContext';
import MemberEditModal from './MemberEditModal';
import OrgChartView from './OrgChartView';
import RolesTab from './RolesTab';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const avatarColor = (name) => {
  const colors = [
    'bg-violet-100 text-violet-700',
    'bg-blue-100 text-blue-700',
    'bg-emerald-100 text-emerald-700',
    'bg-amber-100 text-amber-700',
    'bg-rose-100 text-rose-700',
    'bg-sky-100 text-sky-700',
    'bg-indigo-100 text-indigo-700',
  ];
  const i = (name || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return colors[i % colors.length];
};

const IS_DEV = true;

const RANK = { owner: 3, admin: 2, member: 1 };

const ROLE_CONFIG = {
  owner:  { label: 'Owner',  bg: 'bg-zinc-900', text: 'text-white'    },
  admin:  { label: 'Admin',  bg: 'bg-zinc-200', text: 'text-zinc-700' },
  member: { label: 'Member', bg: 'bg-zinc-100', text: 'text-zinc-500' },
};

const RoleBadge = ({ role }) => {
  const c = ROLE_CONFIG[role] || ROLE_CONFIG.member;
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-lg font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

const SYNTH_NAMES = [
  'Alice Chen', 'Bob Martinez', 'Carol Davis', 'Dave Patel', 'Eva Kim',
  'Frank Nguyen', 'Grace Lee', 'Hank Wilson', 'Iris Brown', 'Jack Taylor',
  'Karen White', 'Leo Garcia', 'Mia Johnson', 'Nate Clark', 'Olivia Hall',
  'Pete Adams', 'Quinn Scott', 'Rosa Evans', 'Sam Baker', 'Tina Young',
];
const SYNTH_TITLES = [
  'Engineer', 'Designer', 'Analyst', 'Manager', 'Coordinator',
  'Specialist', 'Consultant', 'Director', 'Lead', 'Associate',
];
const SYNTH_DEPTS = [
  'Engineering', 'Design', 'Operations', 'Finance', 'Sales', 'Marketing', 'HR',
];
let synthCounter = 0;

const TeamView = () => {
  const { activeCompany } = useAuth();
  const [members, setMembers]               = useState([]);
  const [invites, setInvites]               = useState([]);
  const [roles,   setRoles]                 = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [generating, setGenerating]         = useState(false);
  const [generatedCode, setGeneratedCode]   = useState(null);
  const [copiedCode, setCopiedCode]         = useState(null);
  const [generateError, setGenerateError]   = useState('');
  const [showEmails, setShowEmails]         = useState(false);
  const [activeTab, setActiveTab]           = useState('members');
  const [editingMember, setEditingMember]   = useState(null);

  const myRole = activeCompany?.role;
  const permissions = activeCompany?.permissions || {};
  const canManageTeam = permissions.manage_team || myRole === 'owner';
  const isOwnerOrAdmin = myRole === 'owner' || myRole === 'admin';

  const addSynthMember = () => {
    const idx = synthCounter++;
    const name = SYNTH_NAMES[idx % SYNTH_NAMES.length] + (idx >= SYNTH_NAMES.length ? ` ${Math.floor(idx / SYNTH_NAMES.length) + 1}` : '');
    const id = `synth_${Date.now()}_${idx}`;
    // Randomly assign to an existing member as manager (or root)
    const potential = members.filter(m => m.role !== 'member');
    const reportsTo = potential.length > 0 && Math.random() > 0.3
      ? potential[Math.floor(Math.random() * potential.length)].userId
      : null;
    setMembers(prev => [...prev, {
      userId: id,
      fullName: name,
      email: `${name.toLowerCase().replace(/\s+/g, '.')}@test.com`,
      role: ['admin', 'member', 'member', 'member'][Math.floor(Math.random() * 4)],
      jobTitle: SYNTH_TITLES[Math.floor(Math.random() * SYNTH_TITLES.length)],
      department: SYNTH_DEPTS[Math.floor(Math.random() * SYNTH_DEPTS.length)],
      reportsTo,
      avatarUrl: null,
    }]);
  };

  const clearSynthMembers = () => {
    setMembers(prev => prev.filter(m => !m.userId.startsWith('synth_')));
    synthCounter = 0;
  };

  const TABS = [
    { id: 'members',  label: 'Members'   },
    { id: 'orgchart', label: 'Org Chart' },
    ...(canManageTeam ? [{ id: 'roles', label: 'Roles' }] : []),
  ];

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/companies/members');
        const data = await res.json();
        setMembers(data.members || []);
      } catch { /* ignore */ }
      setLoadingMembers(false);
    })();

    if (canManageTeam) {
      (async () => {
        try {
          const [invRes, rolesRes] = await Promise.all([
            apiFetch('/api/invites'),
            apiFetch('/api/roles'),
          ]);
          const invData   = await invRes.json();
          const rolesData = await rolesRes.json();
          setInvites(invData.invites   || []);
          setRoles(rolesData.roles     || []);
        } catch { /* ignore */ }
        setLoadingInvites(false);
      })();
    } else {
      setLoadingInvites(false);
    }
  }, [canManageTeam]);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerateError('');
    setGeneratedCode(null);
    try {
      const res = await apiFetch('/api/invites', { method: 'POST', body: JSON.stringify({}) });
      const data = await res.json();
      if (!res.ok) { setGenerateError(data.error || 'Failed to generate invite'); return; }
      setGeneratedCode(data.invite.code);
      const invRes = await apiFetch('/api/invites');
      const invData = await invRes.json();
      setInvites(invData.invites || []);
    } catch { setGenerateError('Failed to generate invite'); }
    finally { setGenerating(false); }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(text);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleRevoke = async (inviteId) => {
    try {
      await apiFetch(`/api/invites/${inviteId}`, { method: 'DELETE' });
      setInvites(invites.filter((i) => i.id !== inviteId));
    } catch { /* ignore */ }
  };

  const handleMemberSaved = (updated) => {
    setMembers(prev => prev.map(m => m.userId === updated.userId ? updated : m));
  };

  const loading = loadingMembers || loadingInvites;
  const activeInvites = invites.filter((i) =>
    i.status === 'active' &&
    (!i.expires_at || new Date(i.expires_at) > new Date()) &&
    !(i.max_uses && i.use_count >= i.max_uses)
  );

  // Sort members by rank desc, then name asc
  const sortedMembers = [...members].sort((a, b) => {
    const rd = (RANK[b.role] || 0) - (RANK[a.role] || 0);
    if (rd !== 0) return rd;
    return (a.fullName || '').localeCompare(b.fullName || '');
  });

  return (
    <div className="max-w-[1100px] mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1">Team</p>
          <h1 className="text-2xl font-light text-zinc-900 mb-0.5">{activeCompany?.company?.name || 'Your Company'}</h1>
          <p className="text-sm text-zinc-400">
            {loading ? '—' : `${members.length} member${members.length !== 1 ? 's' : ''}`}
            {canManageTeam && !loading && activeInvites.length > 0 && (
              <span className="ml-2 text-emerald-600">
                · {activeInvites.length} active invite{activeInvites.length !== 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex items-center bg-zinc-100 rounded-xl p-1 gap-0.5">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-1.5 text-[11px] uppercase tracking-widest rounded-lg font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-zinc-900 shadow-sm'
                  : 'text-zinc-400 hover:text-zinc-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-5 h-5 border-2 border-zinc-200 border-t-zinc-700 rounded-full animate-spin" />
        </div>
      ) : activeTab === 'members' ? (
        /* ── MEMBERS TAB ── */
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start">

          {/* Members list */}
          <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-xl shadow-sm">
            <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Members</p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowEmails((v) => !v)}
                  className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-zinc-700 transition-colors"
                >
                  {showEmails ? (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                  {showEmails ? 'Hide' : 'Show'} Emails
                </button>
                <span className="text-[10px] text-zinc-400 tabular-nums">{members.length}</span>
              </div>
            </div>

            {sortedMembers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-12 h-12 rounded-2xl bg-zinc-50 flex items-center justify-center mb-3">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-200">
                    <circle cx="9" cy="7" r="4" /><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75M21 21v-2a4 4 0 0 0-3-3.87" />
                  </svg>
                </div>
                <p className="text-sm text-zinc-400">No members yet</p>
              </div>
            ) : (
              <div className="divide-y divide-zinc-50">
                {sortedMembers.map((m) => {
                  const canEdit = canManageTeam && (RANK[myRole] || 0) > (RANK[m.role] || 0);
                  return (
                    <div
                      key={m.userId}
                      className="flex items-center gap-4 px-5 py-3.5 hover:bg-zinc-50/60 transition-colors duration-100 group"
                    >
                      {m.avatarUrl ? (
                        <img src={m.avatarUrl} alt="" className="w-9 h-9 rounded-xl object-cover shrink-0" />
                      ) : (
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-semibold shrink-0 ${avatarColor(m.fullName || m.email)}`}>
                          {getInitials(m.fullName || m.email?.split('@')[0])}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-zinc-900 truncate">{m.fullName || m.email?.split('@')[0] || 'Unnamed'}</p>
                          {m.jobTitle && (
                            <span className="text-[10px] text-zinc-400 truncate hidden sm:block">· {m.jobTitle}</span>
                          )}
                        </div>
                        {m.department && (
                          <p className="text-[10px] text-zinc-300 truncate">{m.department}</p>
                        )}
                        {m.customRoleName && (
                          <p className="text-[10px] text-indigo-500 truncate">{m.customRoleName}</p>
                        )}
                        {m.email && showEmails && (
                          <p className="text-[11px] text-zinc-400 truncate">{m.email}</p>
                        )}
                      </div>
                      <RoleBadge role={m.role} />
                      {canEdit && (
                        <button
                          onClick={() => setEditingMember(m)}
                          className="ml-1 opacity-0 group-hover:opacity-100 w-7 h-7 flex items-center justify-center rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-all"
                        >
                          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11.5 2.5a2.121 2.121 0 0 1 3 3L5 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right column — invite panel */}
          {canManageTeam && (
            <div className="lg:col-span-2 space-y-4">

              {/* Generate invite */}
              <div className="bg-white border border-zinc-200 rounded-xl shadow-sm">
                <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Invite</p>
                  <button
                    onClick={handleGenerate}
                    disabled={generating}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white text-[10px] uppercase tracking-widest rounded-lg hover:bg-zinc-700 transition-colors disabled:opacity-50"
                  >
                    {generating
                      ? <span className="w-2.5 h-2.5 border border-white border-t-transparent rounded-full animate-spin" />
                      : <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M5 1v8M1 5h8" /></svg>
                    }
                    Generate
                  </button>
                </div>

                {generateError && (
                  <div className="px-5 py-2.5 bg-red-50 text-red-500 text-xs border-b border-red-100">{generateError}</div>
                )}

                {generatedCode ? (
                  <div className="px-5 py-4 bg-emerald-50 border-b border-emerald-100">
                    <p className="text-[10px] uppercase tracking-widest text-emerald-600 font-medium mb-2">New code ready</p>
                    <div className="flex items-center gap-3">
                      <code className="text-xl font-mono font-semibold text-emerald-800 tracking-[0.15em] flex-1">{generatedCode}</code>
                      <button
                        onClick={() => handleCopy(generatedCode)}
                        className="shrink-0 px-3 py-1.5 border border-emerald-200 text-emerald-700 text-[10px] uppercase tracking-widest rounded-lg hover:bg-emerald-100 transition-colors"
                      >
                        {copiedCode === generatedCode ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="px-5 py-4">
                    <p className="text-xs text-zinc-400 leading-relaxed">
                      Generate a one-time code and share it with someone to invite them to your workspace.
                    </p>
                  </div>
                )}
              </div>

              {/* Existing codes */}
              {invites.length > 0 && (
                <div className="bg-white border border-zinc-200 rounded-xl shadow-sm">
                  <div className="px-5 py-4 border-b border-zinc-100">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">All Codes</p>
                  </div>
                  <div className="divide-y divide-zinc-50">
                    {invites.map((inv) => {
                      const isActive = inv.status === 'active' && (!inv.expires_at || new Date(inv.expires_at) > new Date());
                      const isMaxed  = inv.max_uses && inv.use_count >= inv.max_uses;
                      const valid    = isActive && !isMaxed;
                      return (
                        <div key={inv.id} className="flex items-center gap-3 px-5 py-3">
                          <code className={`text-sm font-mono tracking-wider flex-1 ${valid ? 'text-zinc-800' : 'text-zinc-300 line-through'}`}>
                            {inv.code}
                          </code>
                          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-lg font-medium ${valid ? 'bg-emerald-50 text-emerald-600' : 'bg-zinc-100 text-zinc-400'}`}>
                            {!isActive ? 'revoked' : isMaxed ? 'maxed' : 'active'}
                          </span>
                          <span className="text-[11px] text-zinc-400 tabular-nums">{inv.use_count}×</span>
                          {valid && (
                            <div className="flex items-center gap-2">
                              <button onClick={() => handleCopy(inv.code)} className="text-[11px] text-zinc-400 hover:text-zinc-700 transition-colors">
                                {copiedCode === inv.code ? 'Copied!' : 'Copy'}
                              </button>
                              <button onClick={() => handleRevoke(inv.id)} className="text-[11px] text-red-400 hover:text-red-600 transition-colors">
                                Revoke
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : activeTab === 'orgchart' ? (
        /* ── ORG CHART TAB ── */
        <div className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Org Chart</p>
            <div className="flex items-center gap-3">
              {IS_DEV && (
                <>
                  <button
                    onClick={addSynthMember}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-dashed border-zinc-300 text-[10px] uppercase tracking-widest text-zinc-400 rounded-lg hover:border-zinc-500 hover:text-zinc-600 transition-colors"
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M5 1v8M1 5h8" /></svg>
                    Add Test
                  </button>
                  {members.some(m => m.userId.startsWith('synth_')) && (
                    <button
                      onClick={clearSynthMembers}
                      className="px-2.5 py-1 text-[10px] uppercase tracking-widest text-red-400 hover:text-red-600 transition-colors"
                    >
                      Clear Test
                    </button>
                  )}
                </>
              )}
              {isOwnerOrAdmin && (
                <p className="text-[10px] text-zinc-400">Drag cards to reassign reporting lines</p>
              )}
            </div>
          </div>
          <div className="p-6">
            <OrgChartView
              members={members}
              setMembers={setMembers}
              canDrag={isOwnerOrAdmin}
            />
          </div>
        </div>
      ) : (
        /* ── ROLES TAB ── */
        <RolesTab
          roles={roles}
          setRoles={setRoles}
          canManage={canManageTeam}
        />
      )}

      {/* Edit modal */}
      {editingMember && (
        <MemberEditModal
          member={editingMember}
          members={members}
          callerRole={myRole}
          companyRoles={roles}
          onClose={() => setEditingMember(null)}
          onSave={handleMemberSaved}
        />
      )}
    </div>
  );
};

export default TeamView;
